// src/core/pacto.js — capa de datos del Pacto del grupo.
//
// FASE 4 · F1.3.c1. `pacto`, `firmas_pacto` y `propuestas_pacto` viven en
// Supabase Postgres. Las MUTACIONES con reglas (umbral de certificación,
// 7 días de inactividad para tomar control, "solo el editor edita", bump de
// versión al cambiar contenido) pasan por RPCs `SECURITY DEFINER` creados en
// DEV: guardar_pacto, firmar_pacto, certificar_pacto, tomar_control_pacto,
// delegar_pacto. Las propuestas son CRUD directo (la RLS lo permite a
// miembros). Las lecturas son SELECT directo.
//
// `getPactoByGroup` arma un objeto ADAPTADO con la forma que el render de
// pacto.js espera (estado/version/editor_id/tipo/config/firmas/sugerencias):
// `config` lleva el nombre adentro (el render lo lee de config.nombre) y las
// firmas son las de la versión vigente (firmas_pacto.pacto_version).

import { supabase } from '../supabase.js'
import { queryWithTimeout, NET_TIMEOUT_MS } from '../utils/timeout.js'

// categoría: la UI usa MAYÚSCULAS, la columna usa snake_case minúscula.
const CAT_UI = { premio_honor: 'PREMIO_HONOR', premio_pozo: 'PREMIO_POZO', penalidad: 'PENALIDAD' }
const CAT_DB = { PREMIO_HONOR: 'premio_honor', PREMIO_POZO: 'premio_pozo', PENALIDAD: 'penalidad' }

// propuestas_pacto → forma legacy de "sugerencia" que consume el render.
const adaptarPropuesta = (p) => ({
  id: p.propuesta_id,
  user_id: p.player_id,
  categoria: CAT_UI[p.categoria] || p.categoria,
  categoria_val: p.contenido?.categoria_val || '',
  texto_extra: p.contenido?.texto_extra || '',
  texto: p.contenido?.texto || '',
  estado: p.estado,
})

// ─── Lecturas ──────────────────────────────────────────────────────────────

// Pacto de un grupo + firmas (de la versión vigente) + propuestas. NULL si el
// grupo todavía no tiene pacto.
export const getPactoByGroup = async (groupId) => {
  // DT-H3-resto: timeout en los fetches; en fallo/timeout devuelve null (el home
  // muestra "Pacto sin definir" y la sección no cuelga).
  let pacto, error
  try {
    ({ data: pacto, error } = await queryWithTimeout(
      (s) => supabase.from('pacto').select('*').eq('group_id', groupId).abortSignal(s).maybeSingle(),
      NET_TIMEOUT_MS, 'pacto'))
  } catch (err) { console.error('[pacto] getPactoByGroup timeout:', err?.message || err); return null }
  if (error) { console.error('[pacto] getPactoByGroup:', error.message); return null }
  if (!pacto) return null

  let firmas = {}, propuestas = {}
  try {
    [firmas, propuestas] = await Promise.all([
      queryWithTimeout((s) => supabase.from('firmas_pacto').select('player_id, pacto_version').eq('pacto_id', pacto.pacto_id).abortSignal(s), NET_TIMEOUT_MS, 'firmas_pacto'),
      queryWithTimeout((s) => supabase.from('propuestas_pacto').select('*').eq('pacto_id', pacto.pacto_id).abortSignal(s), NET_TIMEOUT_MS, 'propuestas_pacto'),
    ])
  } catch (err) { console.error('[pacto] firmas/propuestas timeout:', err?.message || err) }
  if (firmas.error) console.error('[pacto] firmas:', firmas.error.message)
  if (propuestas.error) console.error('[pacto] propuestas:', propuestas.error.message)

  const firmasVigentes = (firmas.data || [])
    .filter((f) => f.pacto_version === pacto.version)
    .map((f) => f.player_id)

  return {
    pacto_id: pacto.pacto_id,
    group_id: pacto.group_id,
    nombre: pacto.nombre,
    estado: pacto.estado, // 'definir' | 'firmar' | 'activo'
    version: pacto.version,
    editor_id: pacto.editor_id, // player_id
    tipo: pacto.tipo,
    editor_last_activity_at: pacto.editor_last_activity_at,
    config: { ...(pacto.config || {}), nombre: pacto.nombre },
    firmas: firmasVigentes, // player_ids que firmaron la versión vigente
    sugerencias: (propuestas.data || []).map(adaptarPropuesta),
  }
}

export const getPropuestasPacto = async (pactoId) => {
  const { data, error } = await supabase.from('propuestas_pacto').select('*').eq('pacto_id', pactoId)
  if (error) { console.error('[pacto] getPropuestasPacto:', error.message); return [] }
  return (data || []).map(adaptarPropuesta)
}

// ─── Mutaciones del pacto (RPCs SECURITY DEFINER) ──────────────────────────

// INSERT (1ra vez, el caller queda como editor) o UPDATE (solo el editor; el
// cambio de contenido sube versión y resetea firmas, el de nombre no).
export const guardarPacto = async (groupId, nombre, tipo, config) => {
  const { data, error } = await supabase.rpc('guardar_pacto', {
    p_group_id: groupId, p_nombre: nombre, p_tipo: tipo, p_config: config || {},
  })
  return error ? { error } : { data }
}

export const firmarPacto = async (pactoId) => {
  const { data, error } = await supabase.rpc('firmar_pacto', { p_pacto_id: pactoId })
  return error ? { error } : { data }
}

// C3: certificar acepta cupón opcional. Sin cupón (default null) el caller viejo
// sigue andando y el RPC bifurca por config_cobro: OFF → {resultado:'certificado'};
// ON → {resultado:'requiere_pago', orden_id, monto_final_centavos, moneda} (o
// {certificado} si el cupón es 100%).
export const certificarPacto = async (pactoId, codigoCupon = null) => {
  const { data, error } = await supabase.rpc('certificar_pacto', {
    p_pacto_id: pactoId, p_codigo_cupon: codigoCupon,
  })
  return error ? { error } : { data }
}

// C3: validación read-only de cupón (preview sin consumir). Devuelve
// {valido, porcentaje, gratis, motivo}.
export const validarCupon = async (codigo, groupId) => {
  const { data, error } = await supabase.rpc('validar_cupon', {
    p_codigo: codigo, p_group_id: groupId,
  })
  return error ? { error } : { data }
}

export const tomarControlPacto = async (pactoId) => {
  const { data, error } = await supabase.rpc('tomar_control_pacto', { p_pacto_id: pactoId })
  return error ? { error } : { data }
}

export const delegarPacto = async (pactoId, nuevoEditorPlayerId) => {
  const { data, error } = await supabase.rpc('delegar_pacto', {
    p_pacto_id: pactoId, p_nuevo_editor: nuevoEditorPlayerId,
  })
  return error ? { error } : { data }
}

// ─── Propuestas (CRUD directo — la RLS lo permite a miembros) ──────────────

export const crearPropuestaPacto = async (pactoId, playerId, categoriaUI, contenido) => {
  const { data, error } = await supabase
    .from('propuestas_pacto')
    .insert({
      pacto_id: pactoId,
      player_id: playerId,
      categoria: CAT_DB[categoriaUI] || categoriaUI,
      contenido: contenido || {},
    })
    .select()
  return error ? { error } : { data }
}

export const actualizarEstadoPropuesta = async (propuestaId, estado) => {
  const { error } = await supabase
    .from('propuestas_pacto')
    .update({ estado })
    .eq('propuesta_id', propuestaId)
  return error ? { error } : { data: true }
}

export const borrarPropuesta = async (propuestaId) => {
  const { error } = await supabase.from('propuestas_pacto').delete().eq('propuesta_id', propuestaId)
  return error ? { error } : { data: true }
}
